// src/iap/appleIapConfig.js
//
// Single source of truth for Apple In-App Purchase wiring on the iOS
// (Capacitor) build. This exists to satisfy App Store Review Guideline 3.1.1:
// digital goods (coins + memberships) sold INSIDE the iOS app must go through
// Apple In-App Purchase, not Stripe. Web and Android keep using Stripe — see
// `isAppleIap()` below, which is the ONLY gate that flips the buy path.
//
// ────────────────────────────────────────────────────────────────────────────
// BEFORE THIS GOES LIVE you must (see IAP_SETUP.md):
//   1. Create the 16 IAP products in App Store Connect with the EXACT product
//      ids below.
//   2. Create a RevenueCat project, add those products, and paste the iOS
//      public SDK key into REACT_APP_REVENUECAT_IOS_KEY.
// Until REACT_APP_REVENUECAT_IOS_KEY is set, `isAppleIap()` returns false and
// the app transparently falls back to the existing Stripe flow, so shipping
// this code early changes nothing.
// ────────────────────────────────────────────────────────────────────────────

// @capacitor/core is a core dependency (present in every build); on web it
// simply reports getPlatform() === 'web', so this import is safe everywhere.
import { Capacitor } from '@capacitor/core';

// RevenueCat iOS public SDK key (safe to ship in the client — it is a
// publishable key, NOT the secret). Empty string = IAP disabled → Stripe.
export const REVENUECAT_IOS_KEY = String(
  process.env.REACT_APP_REVENUECAT_IOS_KEY || ''
).trim();

// Bundle id of the iOS app (must match capacitor.config.ts `appId`).
export const IOS_BUNDLE_ID = 'com.lextrackai.LexTrack-AI';

// ── Coin bundles → Apple product ids ────────────────────────────────────────
// Keys are the SAME packageId values used everywhere else (COIN_PACKAGES in
// CoinsGiftPanel.jsx and server-lexcoins.js). Prices shown are the REAL prices
// you charge on web — create the App Store Connect products at the same price
// ($10.00, $25.00, … are all valid Apple price points, no .99 rounding).
export const COIN_PRODUCT_IDS = {
  starter:  'com.lextrackai.coins.starter',   // $10   Stone
  plus:     'com.lextrackai.coins.plus',      // $25   Silver
  pro:      'com.lextrackai.coins.pro',       // $50   Gold
  business: 'com.lextrackai.coins.business',  // $100  Platinum
  elite:    'com.lextrackai.coins.elite',     // $250  Titanium
  vault:    'com.lextrackai.coins.vault',     // $500  Diamond
};

// ── Membership tiers × cycle → Apple product ids ────────────────────────────
// Auto-renewable subscriptions. Keys mirror MEMBERSHIP_PRICE_IDS
// (super/creator/pro/business/enterprise) × monthly/yearly.
export const MEMBERSHIP_PRODUCT_IDS = {
  super:      { monthly: 'com.lextrackai.membership.super.monthly',      yearly: 'com.lextrackai.membership.super.yearly' },
  creator:    { monthly: 'com.lextrackai.membership.creator.monthly',    yearly: 'com.lextrackai.membership.creator.yearly' },
  pro:        { monthly: 'com.lextrackai.membership.pro.monthly',        yearly: 'com.lextrackai.membership.pro.yearly' },
  business:   { monthly: 'com.lextrackai.membership.business.monthly',   yearly: 'com.lextrackai.membership.business.yearly' },
  enterprise: { monthly: 'com.lextrackai.membership.enterprise.monthly', yearly: 'com.lextrackai.membership.enterprise.yearly' },
};

export function coinProductId(packageId = '') {
  return COIN_PRODUCT_IDS[String(packageId || '').trim().toLowerCase()] || '';
}

export function membershipProductId(planKey = '', cycle = 'monthly') {
  const key = String(planKey || '').trim().toLowerCase();
  const cyc = cycle === 'yearly' ? 'yearly' : 'monthly';
  return MEMBERSHIP_PRODUCT_IDS[key]?.[cyc] || '';
}

// True ONLY on a native iOS Capacitor build. That plus a configured RevenueCat
// key is the single switch between the IAP path and the Stripe path.
export function isIosNative() {
  try {
    return !!(Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios');
  } catch {
    return false;
  }
}

// THE gate. When false, every caller falls back to the Stripe path unchanged.
export function isAppleIap() {
  return !!REVENUECAT_IOS_KEY && isIosNative();
}
